import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { APP_ENV, OPEN_EXTERNAL_LINK_ACTION } from "app/constants/variables";
import UAParser from "ua-parser-js";

export async function getDeviceId() {
  const fpPromise = FingerprintJS.load();
  const fp = await fpPromise;

  const result = await fp.get();

  return result.visitorId;
}

export function getDeviceName() {
  const parser = new UAParser();
  const result = parser.getResult();

  const osAndBrowsername = result.os.name + " " + result.browser.name;
  return osAndBrowsername;
}

export const IS_IOS = navigator.userAgent.includes("iPhone") || navigator.userAgent.includes("iPad");
export const IS_ANDROID = navigator.userAgent.includes("Android");
export const IS_MOBILE = IS_IOS || IS_ANDROID;

export const getCheckoutDeepLink = (sourceId: string) => {
  if (APP_ENV === "production") {
    if (IS_IOS) {
      return `co.busha.apple://busha.co/checkout?checkoutId=${sourceId}`;
    }
    else if (IS_ANDROID) {
      return `co.busha.android://busha.co/checkout?checkoutId=${sourceId}`;
    }
  } else {
    if (IS_IOS) {
      return `co.busha.boro.development://busha.co/checkout?checkoutId=${sourceId}`;
    } else if (IS_ANDROID) {
      return `co.busha.android.development://busha.co/checkout?checkoutId=${sourceId}`;
    }
  }
}

export interface OpenExternalLinkOptions {
  /**
   * Origin of the embedding parent (`useAppState().parentOrigin`). When this
   * app runs inside an embedding SDK, postMessage is only delivered to this
   * origin. Falls back to "*" if unknown.
   */
  parentOrigin?: string;
}

/**
 * Fire-and-forget: ask the right actor to open an external URL.
 *
 *  - Inside an SDK iframe (`window.parent !== window.self`): postMessage
 *    `{ action: OPEN_EXTERNAL_LINK, url }` to the parent. Both SDKs must
 *    listen for this and open the URL themselves:
 *      • @busha/commerce-js → `window.open(url, "_blank")`
 *      • Busha Flutter SDK  → `url_launcher.launchUrl(uri, externalApplication)`
 *  - Top-level (direct browser tab, or webview hosting this app directly):
 *    `window.open(url, "_blank")`. The Flutter webview handles the resulting
 *    popup via its `onCreateWindow` / `createWebViewWith` delegate. In a
 *    regular browser, the new tab is where the OS intercepts the scheme.
 *
 * Intentionally no detection / no return value: the caller navigates to the
 * web fallback regardless of whether the app actually opens. If the app
 * opened, the user is away and the fallback page simply sits behind them.
 * If it didn't, they're already on the fallback with no wait.
 */
export const openExternalLink = (
  url: string,
  { parentOrigin }: OpenExternalLinkOptions = {}
): void => {
  if (!url) return;

  const isEmbedded = window.parent && window.parent !== window.self;

  if (isEmbedded) {
    try {
      window.parent.postMessage(
        { action: OPEN_EXTERNAL_LINK_ACTION, url },
        parentOrigin || "*"
      );
    } catch {
      // parent may be cross-origin with restrictive settings; nothing to do
    }
    return;
  }

  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    // extremely unlikely; CSP/sandbox would be the only reason
  }
};
